/**
 * azure-functions/shared/sampleRecords.cjs
 *
 * One primitive for telling invented example records apart from real ones
 * (GH #7870, TRYGGR_INTENT.md §5 / §6.2). Shared by the browser (re-exported
 * through src/utils/sampleRecords.js) and the Function App.
 *
 * The Employee lifecycle guide can seed three invented employees and three
 * evaluation criteria into a live tenant. Since GH #7870 every record it seeds
 * carries `sample: true`, and every evaluator, metric, task, sweeper and
 * evidence source reads people through `realEmployees()` (or the whole state
 * through `withoutSampleRecords()`), so invented people never count as
 * coverage or evidence. `npm run sample-records:check` keeps that true for
 * every file that reads employees.
 *
 * Records seeded before the marker existed carry no flag, so a guide-seeded id
 * is a sample only while its fingerprint — id AND name AND email — is still
 * exactly as seeded, flagged or not: the records are editable, and a seed
 * renamed into a real hire must not vanish from evaluators. A seeded record
 * that has been changed is ambiguous: it may now be a real person, so it is
 * COUNTED (excluding a real person under-states the work owed, which
 * over-states compliance). Its seeded evidence (acknowledgements, JML
 * checklists, sensitive-document trails, evaluation scores) is invented
 * whatever the record is now called, so that is stripped either way.
 * `sample: false` is an explicit human decision and always wins over the
 * fingerprint. `sample: true` alone never makes a record a sample — see
 * isSampleEmployee.
 */

'use strict';

// The guide's seed, as shipped in src/components/onboarding/employeeGuideData.js.
// src/utils/sampleRecords.test.js asserts this table still matches that file.
const LEGACY_SAMPLE_EMPLOYEES = Object.freeze({
  'emp-example-1': { name: 'Maria Chen', email: 'maria.chen@example.com' },
  'emp-example-2': { name: 'Thomas Andersen', email: 'thomas.andersen@example.com' },
  'emp-example-3': { name: 'Sarah Olsen', email: 'sarah.olsen@example.com' },
});

const LEGACY_SAMPLE_CRITERIA = Object.freeze({
  'eval-crit-ex-1': 'Security Awareness',
  'eval-crit-ex-2': 'Policy Compliance',
  'eval-crit-ex-3': 'Training Completion',
});

// Nested evidence the seed attached to its employees, by collection.
const LEGACY_SEEDED_NESTED_IDS = Object.freeze({
  documentAcknowledgements: new Set(['ack-ex-1', 'ack-ex-2', 'ack-ex-3', 'ack-ex-4', 'ack-ex-5', 'ack-ex-6']),
  jmlProcesses: new Set(['jml-ex-join-1', 'jml-ex-join-2', 'jml-ex-join-3', 'jml-ex-move-1', 'jml-ex-leave-1']),
  sensitiveDocuments: new Set(['sensdoc-ex-1', 'sensdoc-ex-2', 'sensdoc-ex-3']),
  assets: new Set(['asset-ex-1', 'asset-ex-2', 'asset-ex-3', 'asset-ex-4', 'asset-ex-5', 'asset-ex-6', 'asset-ex-7', 'asset-ex-8']),
  comments: new Set(['cmt-ex-1', 'cmt-ex-2', 'cmt-ex-3', 'cmt-ex-4', 'cmt-ex-5']),
});

const EXCLUDED_SAMPLE_IDS = Symbol.for('tryggr.excludedSampleIds');

// Every tick the seed shipped on its JML processes, as `key|completedAt`. A
// tick is invented only while it is still exactly as seeded: a tick a human
// adds later (or re-ticks with a new date) on a counted former seed is real
// work and counts (GH #7870). src/utils/sampleRecords.test.js asserts this
// table still matches src/components/onboarding/employeeGuideData.js.
const SEEDED_JML_TICKS = Object.freeze({
  'jml-ex-join-1': new Set([
    'contract-signed|2025-06-15T09:00:00Z',
    'nda-signed|2025-06-15T09:15:00Z',
    'screening-complete|2025-06-10T12:00:00Z',
    'it-accounts|2025-06-16T10:00:00Z',
    'assets-issued|2025-06-16T11:00:00Z',
    'security-training|2025-06-20T15:00:00Z',
    'policies-acknowledged|2025-06-22T10:00:00Z',
    'manager-intro|2025-06-15T14:00:00Z',
    'acceptable-use|2025-06-22T10:30:00Z',
  ]),
  'jml-ex-join-2': new Set([
    'contract-signed|2024-03-01T09:00:00Z',
    'nda-signed|2024-03-01T09:30:00Z',
    'screening-complete|2024-02-20T12:00:00Z',
    'it-accounts|2024-03-01T14:00:00Z',
    'assets-issued|2024-03-01T15:00:00Z',
    'security-training|2024-03-10T10:00:00Z',
    'policies-acknowledged|2024-03-12T10:00:00Z',
    'manager-intro|2024-03-01T10:00:00Z',
    'acceptable-use|2024-03-12T10:30:00Z',
  ]),
  'jml-ex-join-3': new Set([
    'contract-signed|2023-01-10T09:00:00Z',
    'nda-signed|2023-01-10T09:15:00Z',
    'screening-complete|2023-01-05T12:00:00Z',
    'it-accounts|2023-01-10T14:00:00Z',
    'assets-issued|2023-01-10T15:00:00Z',
    'security-training|2023-01-18T10:00:00Z',
    'policies-acknowledged|2023-01-20T10:00:00Z',
    'manager-intro|2023-01-10T10:00:00Z',
    'acceptable-use|2023-01-20T10:30:00Z',
  ]),
  'jml-ex-move-1': new Set([
    'access-review|2026-03-21T09:00:00Z',
    'asset-reassignment|2026-03-21T10:00:00Z',
    'new-manager|2026-03-20T11:00:00Z',
  ]),
  'jml-ex-leave-1': new Set([
    'exit-interview|2026-03-25T14:00:00Z',
    'email-forwarding|2026-03-26T10:00:00Z',
    'legal-obligations|2026-03-25T15:00:00Z',
  ]),
});

function norm(v) {
  return typeof v === 'string' ? v.trim().toLowerCase() : '';
}

function hasLegacySampleEmployeeId(e) {
  return !!e && Object.prototype.hasOwnProperty.call(LEGACY_SAMPLE_EMPLOYEES, e.id);
}

/**
 * True when the employee record is certainly invented example data: it carries
 * the explicit marker, or it is an unflagged guide seed still exactly as seeded.
 */
function matchesSeed(e) {
  const seed = LEGACY_SAMPLE_EMPLOYEES[e.id];
  return norm(e.name) === norm(seed.name) && norm(e.email) === norm(seed.email);
}

function isSampleEmployee(e) {
  if (!e || typeof e !== 'object') return false;
  if (e.sample === false) return false;
  // A seeded record — flagged or not — is a sample only while it is still the
  // seed. Renamed into someone else, it may be a real hire, and excluding a
  // real person over-states compliance; it is counted, seeded evidence stripped.
  //
  // `sample: true` on any OTHER record is ignored on purpose: the flag travels
  // on an ordinary client write path, and a flag alone must never be able to
  // hide a real person from every evaluator. Only a fingerprint registered in
  // this file can make a record a sample — a future seeder registers its own.
  return hasLegacySampleEmployeeId(e) && matchesSeed(e);
}

/**
 * A guide-seeded id whose record has since been edited — counted as a real
 * person (fail-safe), but worth showing the user: "this looks like a former
 * sample". Its seeded evidence is stripped by realEmployees().
 */
function isFormerSampleEmployee(e) {
  return !!e && e.sample !== false && hasLegacySampleEmployeeId(e) && !matchesSeed(e);
}

function isSampleCriterion(c) {
  if (!c || typeof c !== 'object') return false;
  if (c.sample === false) return false;
  // As for people: only a registered seed fingerprint, never the flag alone.
  return (
    Object.prototype.hasOwnProperty.call(LEGACY_SAMPLE_CRITERIA, c.id) &&
    norm(c.name) === norm(LEGACY_SAMPLE_CRITERIA[c.id])
  );
}

function isSeededNested(collection, item) {
  if (!item || typeof item !== 'object') return false;
  // Registered seed ids only, never the `sample` flag alone: the flag travels
  // on an ordinary client write path, exactly as it does on people.
  const ids = LEGACY_SEEDED_NESTED_IDS[collection];
  return !!ids && ids.has(item.id);
}

// Seeded nested records split two ways on a person who is counted. Anything
// that SUPPORTS a claim (an acknowledgement, a completed JML process, a
// sensitive-document trail, a returned asset, an evaluation score) is invented
// and is removed — under-claiming is the safe direction. Anything that means
// work is still OWED (an unreturned asset, an open JML process) is kept, with
// its invented ticks cleared, because removing it would make a real leaver
// read as having nothing left to do (TRYGGR_INTENT.md §5).
function seededJmlIsOpen(p) {
  return !!p && p.status !== 'completed' && !p.completedAt;
}

function isInventedTick(p, item) {
  if (!item || !item.completed) return false;
  const seeded = SEEDED_JML_TICKS[p.id];
  return !!seeded && seeded.has(`${item.key}|${item.completedAt}`);
}

// A completed seeded process is the seed's own completion while any of its
// ticks is still exactly as seeded; closed from human ticks alone, it is real.
function seededJmlCompletionIsInvented(p) {
  return Array.isArray(p.checklist) && p.checklist.some((item) => isInventedTick(p, item));
}

function reopenSeededJml(p) {
  if (!Array.isArray(p.checklist) || !p.checklist.some((item) => isInventedTick(p, item))) return p;
  const checklist = p.checklist.map((item) =>
    isInventedTick(p, item)
      ? { ...item, completed: false, completedAt: null, completedBy: null, notes: '' }
      : item,
  );
  return { ...p, checklist };
}

/**
 * A JML process as the evaluators and the sweeper must read it: a seeded open
 * process with its still-as-seeded ticks cleared; anything else unchanged.
 */
function countableJmlProcess(p) {
  return p && isSeededNested('jmlProcesses', p) && seededJmlIsOpen(p) ? reopenSeededJml(p) : p;
}

function stripCollection(collection, list) {
  let changed = false;
  const out = [];
  for (const item of list) {
    if (!isSeededNested(collection, item)) {
      out.push(item);
      continue;
    }
    changed = true;
    if (collection === 'assets' && !item.returnedAt) out.push(item);
    else if (collection === 'jmlProcesses' && seededJmlIsOpen(item)) out.push(reopenSeededJml(item));
    else if (collection === 'jmlProcesses' && !seededJmlCompletionIsInvented(item)) out.push(item);
    // every other seeded record supports a claim: dropped
  }
  return changed ? out : list;
}

/**
 * Strip seeded nested evidence from one (counted) employee. Returns the same
 * object when there is nothing to strip, so memoised consumers keep identity.
 */
function stripSeededEvidence(e) {
  let out = e;
  for (const collection of Object.keys(LEGACY_SEEDED_NESTED_IDS)) {
    const list = e[collection];
    if (!Array.isArray(list)) continue;
    const stripped = stripCollection(collection, list);
    if (stripped === list) continue;
    if (out === e) out = { ...e };
    out[collection] = stripped;
  }
  // Scores the seed recorded against its own criteria on a seeded person.
  if (hasLegacySampleEmployeeId(e) && e.evaluations && typeof e.evaluations === 'object') {
    const keys = Object.keys(e.evaluations).filter((k) =>
      Object.prototype.hasOwnProperty.call(LEGACY_SAMPLE_CRITERIA, k),
    );
    if (keys.length) {
      if (out === e) out = { ...e };
      const evaluations = { ...e.evaluations };
      for (const k of keys) delete evaluations[k];
      out.evaluations = evaluations;
    }
  }
  return out;
}

/**
 * The employees that may count as people, coverage or evidence: sample people
 * removed, seeded evidence stripped from the rest. Returns the same array when
 * nothing changes.
 */
function realEmployees(list) {
  if (!Array.isArray(list)) return [];
  let changed = false;
  const out = [];
  const excluded = new Set(list[EXCLUDED_SAMPLE_IDS] || []);
  for (const e of list) {
    if (isSampleEmployee(e)) {
      changed = true;
      excluded.add(e.id);
      continue;
    }
    const stripped = e && typeof e === 'object' ? stripSeededEvidence(e) : e;
    if (stripped !== e) changed = true;
    out.push(stripped);
  }
  if (!changed) return list;
  // Remember who was removed, so a requirement that NAMES a sample person (a
  // document's specific readers) can still read as unmet instead of silently
  // disappearing. Non-enumerable: never serialised, never iterated.
  Object.defineProperty(out, EXCLUDED_SAMPLE_IDS, { value: excluded, enumerable: false });
  return out;
}

/** Ids a realEmployees() result removed as samples (empty for a raw list). */
function excludedSampleIds(list) {
  return (Array.isArray(list) && list[EXCLUDED_SAMPLE_IDS]) || new Set();
}

function realEvaluationCriteria(list) {
  if (!Array.isArray(list)) return [];
  return list.some(isSampleCriterion) ? list.filter((c) => !isSampleCriterion(c)) : list;
}

/**
 * The tenant state as every evaluator must see it. Returns the same object when
 * the state holds no sample or seeded record.
 */
function withoutSampleRecords(state) {
  if (!state || typeof state !== 'object') return state;
  const employees = Array.isArray(state.employees) ? realEmployees(state.employees) : state.employees;
  const criteria = Array.isArray(state.employeeEvaluationCriteria)
    ? realEvaluationCriteria(state.employeeEvaluationCriteria)
    : state.employeeEvaluationCriteria;
  if (employees === state.employees && criteria === state.employeeEvaluationCriteria) return state;
  return { ...state, employees, employeeEvaluationCriteria: criteria };
}

/** Ids of the sample employees in a list — for dropping stored references. */
function sampleEmployeeIds(list) {
  const ids = new Set();
  if (Array.isArray(list)) for (const e of list) if (isSampleEmployee(e)) ids.add(e.id);
  return ids;
}

/** Deep-stamp `sample: true` on a seeded employee and its nested records. */
function markSampleEmployee(e) {
  const out = { ...e, sample: true };
  for (const collection of Object.keys(LEGACY_SEEDED_NESTED_IDS)) {
    if (Array.isArray(e[collection])) out[collection] = e[collection].map((item) => ({ ...item, sample: true }));
  }
  return out;
}

function markSampleCriterion(c) {
  return { ...c, sample: true };
}

module.exports = {
  LEGACY_SAMPLE_EMPLOYEES,
  LEGACY_SAMPLE_CRITERIA,
  LEGACY_SEEDED_NESTED_IDS,
  isSampleEmployee,
  isFormerSampleEmployee,
  isSampleCriterion,
  realEmployees,
  excludedSampleIds,
  isSeededNestedRecord: isSeededNested,
  countableJmlProcess,
  SEEDED_JML_TICKS,
  stripSeededEvidence,
  realEvaluationCriteria,
  withoutSampleRecords,
  sampleEmployeeIds,
  markSampleEmployee,
  markSampleCriterion,
};
